import { useEffect, useMemo, useState } from 'react';

import type { IDayObject, IView } from './types';

import useLanguage from '@snowit/system/useLanguage';
import executer from '@snowit/utils/executer';
import snowmentV2 from '@snowit/utils/snowmentv2';
import testId from '@snowit/utils/testId';

import { Typo } from '../../';
import ButtonIcon from '../../Buttons/ButtonIcon';
import Loader from '../../Loaders/LoaderSpinner';
import CalendarDay from './CalendarDay';
import CalendarWeekdays from './CalendarWeekdays';
import useCalendar from './useCalendar';

import cx from 'classnames';
import s from './Calendar.module.scss';

export interface ICalendarWeek {
  activeStartDate?: string;
  loading?: boolean;
  maxDate?: string;
  maxDays?: number | null;
  minDate?: string;
  minDays?: number;
  mode?: 'range' | 'single';
  onChange: (date: string) => void;
  onChangeView?: (date: string) => void;
  onlyDates?: string[];
  test?: string;
  value: string[];
  availability?: Record<string, string>;
  className?: string;
  stockDisabled?: boolean;
}

const CalendarWeek = (props: ICalendarWeek) => {
  const {
    activeStartDate,
    loading,
    maxDate,
    minDate,
    onChange,
    onChangeView,
    onlyDates,
    test,
    value,
    availability,
    className,
    stockDisabled,
  } = props;

  const { t } = useLanguage();

  const values = (Array.isArray(value) ? value : [value]).filter(Boolean);
  const first = values[0];
  const last = values[values.length - 1];

  const ranges = useMemo(() => {
    // get the full range
    const fullRange = snowmentV2.enumerateDays(first, last);
    // set an index to start with
    let index = 0;
    // a = all, c = current, i = index, o = original
    return fullRange.reduce((a: string[][], c: string, i: number, o: string[]) => {
      // get the previous date in the array (if exists)
      const prev = o[i - 1];
      // the the enxt date in the array (if exists)
      const next = o[i + 1];
      // if values do not include the previous, but includes the value
      // set the new index to slice the array with
      if (!values.includes(prev) && values.includes(c)) {
        index = i;
      }
      // if the previous exists and is included into the values,
      // but the current one is out of range, slice the array
      if (prev && values.includes(prev) && !values.includes(c)) {
        return [...a, o.slice(index, i)];
      }
      // if there is no next one it means we are at the end of the array
      if (!next && values.includes(c)) {
        return [...a, o.slice(index, i + 1)];
      }
      // otherwise do nothing and return what we have
      return a;
    }, [] as string[][]);
  }, [values, first, last]);

  const [view, setView] = useState<string>(
    first || activeStartDate || minDate || snowmentV2().format(),
  );

  useEffect(() => {
    if (activeStartDate) {
      setView(activeStartDate);
    }
  }, [activeStartDate]);

  const { current, weekdays } = useCalendar(view, { weekly: true });

  const isDayDisabled = (day: IDayObject, onlyVisual: boolean) => {
    const { value: d } = day;
    if (maxDate && snowmentV2(maxDate).isBefore(d, 'day')) {
      return true;
    }
    if (minDate && snowmentV2(minDate).isAfter(d, 'day')) {
      return true;
    }
    if (onlyVisual && Array.isArray(value) && value.includes(d)) return false;
    if (Array.isArray(onlyDates) && !onlyDates.includes(d)) {
      return true;
    }
    return false;
  };

  const handleDayClick = (day: IDayObject) => {
    if (isDayDisabled(day, false)) return;
    onChange(day.value);
  };

  const isButtonDisabled = () => {
    const newDate = snowmentV2(view).subtract(1, 'weeks');
    if (snowmentV2(newDate.format()).isBefore(snowmentV2().format(), 'day')) return true;
    return false;
  };

  const onPrevWeek = () => {
    const newDate = snowmentV2(view).subtract(1, 'weeks').format();
    setView(newDate);
    executer(onChangeView, newDate);
  };

  const onNextWeek = () => {
    const newDate = snowmentV2(view).add(1, 'weeks').format();
    setView(newDate);
    executer(onChangeView, newDate);
  };

  const renderHeader = (thisView: IView) => {
    return (
      <div className={s.header}>
        <ButtonIcon
          classNames={{ button: s.prevBtnIcon }}
          icon={'arrow_angle_left'}
          onClick={onPrevWeek}
          disabled={isButtonDisabled()}
          theme={'ghost'}
          size={'db'}
          color={'text'}
          a11y={{ label: 'Previous week' }}
          test={'prev-week-icon'}
        />
        <div className={s.wrapTitle}>
          <Typo text={t('lzy.label.week')} tag={'span'} size={'sm'} color={'text'} />
          <Typo text={thisView.label || ''} tag={'span'} size={'md'} color={'text'} />
        </div>
        <ButtonIcon
          classNames={{ button: s.nextBtnIcon }}
          icon={'arrow_angle_right'}
          onClick={onNextWeek}
          theme={'ghost'}
          size={'db'}
          color={'text'}
          a11y={{ label: 'Next week' }}
          test={'next-week-icon'}
        />
      </div>
    );
  };

  const renderLoading = () => {
    if (!loading) return null;
    return (
      <div className={s.loaderOverlay}>
        <Loader className={s.loaderIcon} size={24} />
        <p className={s.loaderLabel}>{t('lzy.loading.check_availability.text')}</p>
      </div>
    );
  };

  const renderWeekdays = () => {
    return <CalendarWeekdays weekdays={weekdays} />;
  };

  const renderCell = (day: IDayObject) => {
    const disabled = isDayDisabled(day, true);
    return (
      <CalendarDay
        key={day.value}
        day={day}
        onClick={handleDayClick}
        ranges={ranges}
        disabled={disabled}
        availability={availability?.[day.value]}
        stockDisabled={stockDisabled}
      />
    );
  };

  const renderDaysCells = (days: IDayObject[]) => {
    return <div className={s.weekDays}>{days.slice(0, 7).map(renderCell)}</div>;
  };

  const renderDayBlocks = (daysView?: IView) => {
    if (!daysView) return null;
    const { days } = daysView;

    return (
      <div className={s.dayBlock}>
        {renderLoading()}
        {renderWeekdays()}
        {renderDaysCells(days)}
      </div>
    );
  };

  const renderContent = () => {
    return <div className={s.wrapDayBlock}>{renderDayBlocks(current)}</div>;
  };

  return (
    <div className={cx(s.calendar, className)} {...testId(test)}>
      {renderHeader(current)}
      {renderContent()}
    </div>
  );
};

export default CalendarWeek;
